import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@@/shared/utils/cn";
import { jsx } from "@emotion/react";
import { faSpinnerThird } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setWithinPercentageRange } from "@towni/common";
import { useCallback, useRef } from "react";
import type { RenderButtonAs } from "../buttons_v2/button";
import { Row } from "./row";

const defaultButtonClickConfettiOptions = (
    x: number,
    y: number,
): confetti.Options => {
    const centerXPercent = x
        ? setWithinPercentageRange(x / window.innerWidth)
        : 0.5;
    const centerYPercent = y
        ? setWithinPercentageRange(y / window.innerHeight)
        : 0.5;
    return {
        particleCount: 10,
        shapes: ["star", "circle"],
        spread: 90,
        startVelocity: 10,
        ticks: 30,
        origin: {
            x: centerXPercent,
            y: centerYPercent,
        },
    };
};

// Define a more specific type for onClick
type OnClickWithBoundingBox<HTMLElementType extends HTMLButtonElementTypes> = (
    event: React.MouseEvent<HTMLElementType>,
    boundingBox: {
        height: number;
        width: number;
        top: number;
        right: number;
        bottom: number;
        left: number;
        x: number;
        y: number;
        centerX: number;
        centerY: number;
    },
) => void;

type OnClickSimple<HTMLElementType extends HTMLButtonElementTypes> = (
    event: React.MouseEvent<HTMLElementType>,
) => void;

// Variants
// These are the variants that are used in the button component
// They are used to determine the styles of the button based on the variant prop
const whiteClassNames =
    "bg-neutral border-1 border-neutral-outline text-neutral-copy-2 shadow-xs hover:bg-neutral-020 hover:border-neutral-200";
const whitePrimaryClassNames =
    "bg-neutral border-1 border-neutral-outline text-primary shadow-xs hover:bg-neutral-020 hover:border-neutral-200";
const whiteGrayClassNames =
    "bg-neutral border-1 border-neutral-outline text-neutral-copy-4 shadow-xs hover:bg-neutral-020 hover:border-neutral-200";
const lightGrayBlackClassNames =
    "bg-neutral-050 border-1 border-neutral-050 text-neutral-copy shadow-xs hover:bg-neutral hover:border-neutral";
const dangerClassNames =
    "bg-danger text-white border-1 border-danger shadow-xs hover:bg-danger/90 focus-visible:ring-danger/50 dark:focus-visible:ring-danger/30 dark:bg-danger/60";
const dangerLightClassNames =
    "bg-danger-100 text-danger border-1 border-danger-200 shadow-xs hover:bg-danger-200 focus-visible:ring-danger/30 dark:focus-visible:ring-danger/40 dark:bg-danger/60";
const outlineClassNames =
    "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50";
const primaryClassNames =
    "bg-primary border-1 border-primary text-white shadow-xs hover:bg-primary-600";
const primaryLightClassNames =
    "bg-primary-100 text-primary shadow-xs hover:bg-primary-200 border-1 border-primary-500/30 hover:border-primary-500/50";
const purpleLightClassNames =
    "bg-purple-100 text-purple shadow-xs hover:bg-purple-150 border-1 border-purple-100 hover:border-purple-150";
const secondaryClassNames =
    "bg-secondary border-1 border-secondary text-white shadow-xs hover:bg-secondary/80";
const yellowClassNames =
    "bg-tertiary-100 text-neutral-600 border-1 border-tertiary-600 shadow-xs hover:bg-tertiary-200";
const ghostClassNames =
    "border-1 border-transparent hover:border-accent hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50";
const transparentClassNames = "border-1 border-transparent";
const linkClassNames =
    "text-primary border-1 border-transparent underline-offset-4 hover:underline p-0 m-0";
const warningClassNames =
    "bg-warning-surface-10 rounded-sm text-warning border-1 border-warning-400 shadow-xs hover:bg-warning-200";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive active:shadow-none",
    {
        variants: {
            variant: {
                default: whiteClassNames,
                white: whiteClassNames,
                "white-gray": whiteGrayClassNames,
                "white-primary": whitePrimaryClassNames,
                danger: dangerClassNames,
                "danger-light": dangerLightClassNames,
                outline: outlineClassNames,
                primary: primaryClassNames,
                "primary-light": primaryLightClassNames,
                "purple-light": purpleLightClassNames,
                secondary: secondaryClassNames,
                yellow: yellowClassNames,
                ghost: ghostClassNames,
                link: linkClassNames,
                warning: warningClassNames,
                transparent: transparentClassNames,
                "light-gray-black": lightGrayBlackClassNames,
            },
            size: {
                default: "h-9 p-6 rounded-md text-base ",
                md: "h-9 p-6 rounded-md text-base",
                sm: "h-8 rounded-md gap-1.5 p-4 py-4.5 text-base",
                xs: "h-6 rounded-md gap-1.5 p-3.5 text-sm",
                "2xs": "rounded-sm gap-1.5 px-2 py-1.5 text-xs",
                "3xs": "rounded-[5px] gap-1.5 px-1.5 py-0.5 text-2xs font-bold",
                lg: "h-10 rounded-sm gap-2.5 px-5.5 py-7 text-lg",
                icon: "size-9",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

type HTMLButtonElementTypes =
    | HTMLButtonElement
    | HTMLAnchorElement
    | HTMLDivElement;

type Props<HTMLElementType extends HTMLButtonElementTypes> =
    React.ComponentProps<"button"> &
        VariantProps<typeof buttonVariants> & {
            renderAs?: RenderButtonAs;
            dataTestId?: string;
            confetti?: boolean | confetti.Options;
            spin?: boolean;
            allowPassthroughClicks?: boolean;
            onClick?:
                | OnClickWithBoundingBox<HTMLElementType>
                | OnClickSimple<HTMLElementType>;
        };
/**
 * Renders a customizable button component with support for variants, sizes, and custom class names.
 *
 * @param className - Additional CSS classes to apply to the button.
 * @param variant - The visual style variant of the button, as defined in `buttonVariants`.
 * @param size - The size of the button, as defined in `buttonVariants`.
 * @param asChild - If true, renders the button as a child component using `Slot` instead of a native `<button>`.
 * @param props - Additional props passed to the underlying button or child component.
 *
 * @returns A button or custom component with the specified styles and props.
 */
function Button<
    T extends RenderButtonAs = "button",
    HTMLElementType extends HTMLButtonElementTypes = T extends "a"
        ? HTMLAnchorElement
        : T extends "div"
          ? HTMLDivElement
          : HTMLButtonElement,
>(props: Props<HTMLElementType>) {
    const {
        onClick: propsOnClick,
        confetti,
        renderAs = "button",
        variant,
        size,
        spin,
        className,
        children,
        allowPassthroughClicks,
        ...passOnProps
    } = props;
    const Comp = renderAs;
    const disabled = !!(props.disabled || props.spin);
    const onClickRef = useRef(propsOnClick);
    onClickRef.current = propsOnClick;
    const onClick = useCallback(
        (event: React.MouseEvent<HTMLElementType>) => {
            if (!onClickRef.current) return;
            event.preventDefault();
            if (!allowPassthroughClicks) {
                event.stopPropagation();
            }
            if (disabled) return;
            if (confetti) {
                void window.confetti(
                    typeof confetti !== "boolean"
                        ? confetti
                        : defaultButtonClickConfettiOptions(
                              event.clientX,
                              event.clientY,
                          ),
                );
            }

            if (onClickRef.current.length > 1) {
                const boundingBox = event.currentTarget.getBoundingClientRect();
                onClickRef.current(event, {
                    height: boundingBox.height,
                    width: boundingBox.width,
                    top: boundingBox.top,
                    right: boundingBox.right,
                    bottom: boundingBox.bottom,
                    left: boundingBox.left,
                    x: boundingBox.x,
                    y: boundingBox.y,
                    centerX: boundingBox.left + boundingBox.width / 2,
                    centerY: boundingBox.top + boundingBox.height / 2,
                });
            } else {
                (onClickRef.current as OnClickSimple<HTMLElementType>)(event);
            }
        },
        [allowPassthroughClicks, confetti, disabled],
    );

    return jsx(Comp, {
        dataslot: "button",
        datatestid: props["dataTestId"],
        className: cn(
            "cursor-pointer grid [grid-template-areas:'stack']",
            buttonVariants({ variant, size, className }),
        ),
        onClick,
        children: (
            <>
                <Row
                    mainAxis="center"
                    crossAxis="center"
                    className={cn("size-full [grid-area:stack]", {
                        hidden: !spin,
                    })}>
                    <FontAwesomeIcon
                        icon={faSpinnerThird}
                        fixedWidth={true}
                        className="text-[120%]"
                        spin
                    />
                </Row>
                <div
                    className={cn("contents [grid-area:stack]", {
                        hidden: !!spin,
                    })}>
                    {children}
                </div>
            </>
        ),
        ...passOnProps,
        disabled: !!disabled,
    });
}

export { Button, buttonVariants };
